// PayPal adapter — future mercadopago-sdk.ts will follow same interface
// Adapter agnóstico: PaymentGatewayFactory en backend; este SDK es solo PayPal.
// Future adapter mercadopago-sdk.ts should expose loadSDK(provider)+renderButton(provider,{amount,currency})
/**
 * Helper del SDK moderno de PayPal (TypeScript plano, sin dependencias de Angular).
 *
 * - loadPayPalSDK: carga el script del SDK una sola vez (idempotente, script id `paypal-sdk-js`).
 * - renderPayPalButton: renderiza el boton de pago con el monto/divisa recibidos por configuracion.
 *   Soporta breakdown impuestos: si taxAmount>0 incluye breakdown {item_total, tax_total} con totalAmount.
 *
 * Flujo popup-only: el enrutado por hash (useHash) rompe el return_url del flujo de redireccion.
 */

const PAYPAL_SDK_SCRIPT_ID = 'paypal-sdk-js';

export interface PayPalButtonOptions {
  amount: string;
  currency: string;
  planAmount?: string;
  taxAmount?: string;
  onApprove: (orderId: string) => void;
  onError: (err: any) => void;
  onCancel: () => void;
  onClick?: () => void;
}

/**
 * Carga https://www.paypal.com/sdk/js?client-id=... una unica vez.
 * Resuelve true cuando el SDK quedo disponible, false si falla la carga o falta clientId.
 * Incluye card-fields pre-cargados para evitar delay al expandir "Tarjeta".
 */
export function loadPayPalSDK(clientId: string, currency?: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!clientId) {
      resolve(false);
      return;
    }

    const existing = document.getElementById(PAYPAL_SDK_SCRIPT_ID) as HTMLScriptElement;
    if (existing) {
      if ((window as any).paypal) {
        resolve(true);
      } else {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => resolve(false));
      }
      return;
    }

    const script = document.createElement('script');
    script.id = PAYPAL_SDK_SCRIPT_ID;
    let url = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(clientId);
    url += '&components=buttons,card-fields';
    url += '&enable-funding=card';
    url += '&currency=' + encodeURIComponent(currency || 'USD');
    script.src = url;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

/**
 * Renderiza el boton de PayPal dentro del contenedor indicado.
 * onApprove recibe el orderID v2 (verificable por el gateway del backend).
 * Devuelve false si el SDK o el contenedor no estan disponibles.
 */
export function renderPayPalButton(containerId: string, opts: PayPalButtonOptions): boolean {
  const wPaypal: any = (window as any).paypal;
  const container = document.getElementById(containerId);
  if (!wPaypal || !wPaypal.Buttons || !container) {
    return false;
  }

  wPaypal.Buttons({
    style: {layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal'},
    onClick: () => {
      if (opts.onClick) opts.onClick();
    },
    createOrder: (data: any, actions: any) => {
      var taxNum = opts.taxAmount ? Number(opts.taxAmount) : 0;
      var hasTax = taxNum > 0 && !!opts.planAmount;
      if (hasTax) {
        var totalVal = Number(opts.amount).toFixed(2);
        var planVal = Number(opts.planAmount).toFixed(2);
        var taxVal = Number(opts.taxAmount).toFixed(2);
        return actions.order.create({
          purchase_units: [{
            amount: {
              value: totalVal,
              currency_code: opts.currency,
              breakdown: {
                item_total: {value: planVal, currency_code: opts.currency},
                tax_total: {value: taxVal, currency_code: opts.currency}
              }
            }
          }]
        });
      }
      return actions.order.create({
        purchase_units: [{
          amount: {
            value: opts.amount,
            currency_code: opts.currency
          }
        }]
      });
    },
    onApprove: (data: any) => {
      opts.onApprove(data.orderID || data.paymentID);
    },
    onError: (err: any) => {
      opts.onError(err);
    },
    onCancel: () => {
      opts.onCancel();
    }
  }).render('#' + containerId);
  return true;
}



// WEBPACK FOOTER //
// ./src/app/shared/paypal-sdk.ts